<template>
  <div class="banking-information-step">

    <div class="fm-info-banner mb-4">
      <svg fill="currentColor" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"/>
      </svg>
      <span>Your bank account details are used for disbursement of the loan funds only.</span>
    </div>

    <div class="row">
      <div class="col-md-12">
        <FormGroup label="Account Name" :error-container="errorContainerName" name="accountName" :floating="true">
          <input :value="form.accountName" class="form-control" type="text" readonly placeholder=" "/>
        </FormGroup>
      </div>
      <div class="col-md-6">
        <FormGroup label="BSB" :error-container="errorContainerName" name="bsb" :floating="true">
          <input v-model="form.bsb" class="form-control" type="text" placeholder=" "/>
        </FormGroup>
      </div>
      <div class="col-md-6">
        <FormGroup label="Account Number" :error-container="errorContainerName" name="accountNumber" :floating="true">
          <input v-model="form.accountNumber" class="form-control" type="text" placeholder=" "/>
        </FormGroup>
      </div>
    </div>

    <slot name="controls" v-bind="{ prev: onPrev, next: onNext }"/>
  </div>
</template>

<script>
import FormGroup from '@modules/shared/components/form/FormGroup';
import {NEW_APPLICATION} from '@store/constants';
import NewApplicationStepMixin from '@modules/guest/new-applicant/components/mixins/NewApplicationStepMixin';
import {validate} from './formValidator';
import {scrollToErrorFormField} from '@modules/shared/helpers/form';
import store from '@modules/guest/new-applicant/store/store';
import {saveAsDraft} from '@modules/guest/new-applicant/NewApplicationService';
import {mergeDeep} from '@modules/shared/helpers/object-fns';

const ERROR_CONTAINER_NAME = 'NewApplicationFinancialCapacityStepForm';

const defaultFormState = () => ({ accountName: '', bsb: '', accountNumber: '' });

export default {
  name: 'BankingInformation',
  mixins: [NewApplicationStepMixin],
  components: {FormGroup},
  data() {
    const original = store.state[NEW_APPLICATION.MODULE_NAME].cache ?? {};
    return { form: mergeDeep(defaultFormState(), original.meta?.BankingInformation ?? {}) };
  },
  computed: {
    code() { return store.state[NEW_APPLICATION.MODULE_NAME].code; },
    accountName() {
      try {
        return store.state[NEW_APPLICATION.MODULE_NAME].cache?.meta?.BankingInformation?.accountName;
      } catch (e) { return null; }
    },
  },
  watch: {
    accountName: {
      immediate: true,
      handler() { if (this.accountName) this.form.accountName = this.accountName; },
    },
    form: {
      deep: true, immediate: true,
      handler() { if (this.shouldGoNextStep()) this.syncWithStore(); },
    },
  },
  methods: {
    onNext() {
      if (!this.shouldGoNextStep()) { scrollToErrorFormField(this.$el); return; }
      this.saveAndSync().then(async () => {
        await store.dispatch(NEW_APPLICATION.ACTIONS.NEXT_STEP);
      }).catch(e => { this.handleServerError(e); scrollToErrorFormField(this.$el); });
    },
    onPrev() { store.dispatch(NEW_APPLICATION.ACTIONS.PREV_STEP); },
    async saveAndSync() {
      const payload = { step: 'BankingInformation', ...this.form };
      store.commit({type: NEW_APPLICATION.MUTATIONS.SET_STEPS_META, payload: {loading: true}});
      try {
        const {data: {draft, otp, loanNumber, canPerformCS, vars}} = await saveAsDraft(this.code, payload);
        store.commit({type: NEW_APPLICATION.MUTATIONS.SET_META, payload: {otp, loanNumber, canPerformCS}});
        store.commit({type: NEW_APPLICATION.MUTATIONS.SET_VARS, payload: vars});
        store.commit({type: NEW_APPLICATION.MUTATIONS.SET_CACHE, payload: draft});
      } catch (e) { throw e; }
      finally { store.commit({type: NEW_APPLICATION.MUTATIONS.SET_STEPS_META, payload: {loading: false}}); }
    },
    syncWithStore() {
      this.$store.commit({ type: NEW_APPLICATION.MUTATIONS.SET_FINANCIAL_CAPACITY, payload: { ...this.form } });
    },
    validate,
  },
  created() { this.registerErrorContainer(ERROR_CONTAINER_NAME); },
};
</script>

<style lang="scss">
.fm-info-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: #EFF6FF;
  border: 1px solid rgba(59, 130, 246, 0.25);
  border-radius: 10px;
  font-size: 13.5px;
  color: #1d4ed8;

  svg { flex-shrink: 0; width: 16px; height: 16px; }
}
</style>
